import UserService from "./UserService";
import ConfigurationService from "./ConfigurationService";

/**
 * Redirect to /login and clear the stored session when the API rejects a token.
 *
 * This mirrors the 401 interceptor that KichiriService installs for the rest of
 * the app (see KichiriService.checkIfUnauthorizedThenLogout). The tools services
 * (AFS / Bookkeeping / Gmail / Reconciliation) call the API with raw fetch, so
 * without this they would silently swallow an expired session instead of logging
 * the user out like every Kichiri-backed screen does. Kept dependency-free of the
 * service graph on purpose to avoid an import cycle (apiFetch → UserService →
 * KichiriService).
 */
function handleUnauthorized() {
	if (window.location.pathname !== "/login") {
		window.location.href = "/login";
	}
	localStorage.removeItem("workflow_user");
}

/**
 * fetch() wrapper for the raw-fetch tools services. Resolves the API base URL,
 * injects the user's auth token, and routes 401s through handleUnauthorized() so
 * auth behaviour matches the Kichiri-backed parts of the app.
 *
 * Returns the raw Response untouched, so callers keep using `.json()` / `.blob()`
 * exactly as before. Content-Type is intentionally NOT set here, so FormData
 * uploads still get the browser-generated multipart boundary — pass it yourself
 * via `options.headers` for JSON bodies (as the call sites already do).
 *
 * @param {String} path     API path beginning with "/" (e.g. "/tools/afs/generate")
 * @param {Object} [options] Standard fetch options (method, body, headers, ...)
 * @return {Promise<Response>}
 */
export async function authedFetch(path, options = {}) {
	const userData = UserService.getUserData() || {};

	const response = await fetch(`${ConfigurationService.resolveApi().rest}${path}`, {
		...options,
		headers: {
			authorization: userData.authToken,
			...(options.headers || {}),
		},
	});

	if (response.status === 401) {
		handleUnauthorized();
	}

	return response;
}

export const COULD_NOT_REACH_THE_SERVER = "The server could not be reached.";

/**
 * Says what a server did when it sent something that was not a result.
 *
 * The status is the whole of what is known here, so it is what gets said. Guessing beyond it —
 * blaming the request, or the file the request carried — is how a deploy problem comes to be
 * reported as a bad document.
 *
 * @param {Object} params
 * @param {Response} [params.response]
 * @returns {String}
 */
function describeAReplyThatWasNotAResult({ response }) {
	const status = response ? response.status : 0;

	if (status === 404) return "The server does not have this feature (404) — it is likely running an older build than this screen.";
	if (status === 413) return "The server refused the upload as too large (413).";
	if (status === 502 || status === 504) return `The server did not finish this in time (${status}).`;
	if (status >= 500) return `The server failed while handling this (${status}).`;
	if (status > 0) return `The server's reply could not be read (${status}).`;

	return COULD_NOT_REACH_THE_SERVER;
}

/**
 * The JSON a request answered with, or a plain statement of why there is none.
 *
 * `response.json()` throws on anything that is not JSON, and a server that is missing a route, or
 * sitting behind a proxy that gave up, answers with an HTML error page. Every caller used to catch
 * that and report it as a problem with whatever the user had supplied — a statement that could not
 * be parsed, an export that could not be read — which sends someone to re-examine a file that was
 * never involved. That is what happened when the stage API was a build behind: six good statements
 * were reported as unreadable while the endpoint behind them was answering 404.
 *
 * A server that failed and a document that cannot be read are different facts and now read
 * differently. An error the API itself reports is passed through as it was written, because the
 * API knows more about it than this does.
 *
 * @param {Object} params
 * @param {Response} params.response
 * @returns {Promise<Object>} the parsed body, or { error }
 */
export async function readJsonAnswer({ response }) {
	let body = null;
	try {
		body = await response.json();
	} catch (error) {
		return { error: describeAReplyThatWasNotAResult({ response }) };
	}

	if (!body) return { error: describeAReplyThatWasNotAResult({ response }) };
	if (body.error) return { error: String(body.error).replace("Error: ", "") };

	return body;
}
